/**
 * SaveButtonWithHint — centralised Save button with a visible hint explaining
 * why the button is disabled. Used by all 5 edit surfaces that gate on an
 * audit comment (CycleScheduleEditor + 4 TypedFieldsEditor sub-fields).
 *
 * Props:
 *   isPending      — mutation in flight
 *   isDirty        — whether the form value has changed from its initial state
 *   missingComment — whether the audit comment is empty
 *   blockedByAck   — (optional) extra gate, e.g. "must acknowledge a confirmed obligation"
 *   onClick        — called when the button is clicked (and not disabled)
 *   saveLabel      — button text in the enabled state
 *   hintIds        — i18n key suffixes surfaced through the common namespace
 *
 * Accessibility: the hint <p> has an id that is wired to the button via
 * aria-describedby so screen readers announce the reason.
 */

import { useId } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";

export interface SaveButtonWithHintProps {
  isPending: boolean;
  isDirty: boolean;
  missingComment: boolean;
  blockedByAck?: boolean;
  onClick: () => void;
  saveLabel: string;
  className?: string;
}

export function SaveButtonWithHint({
  isPending,
  isDirty,
  missingComment,
  blockedByAck = false,
  onClick,
  saveLabel,
  className,
}: SaveButtonWithHintProps): JSX.Element {
  const { t } = useTranslation("common");
  const hintId = useId();

  // Resolve the active hint message (first matching reason wins).
  let hintText: string | null = null;
  if (isPending) {
    hintText = t("save_hint_saving");
  } else if (!isDirty) {
    hintText = t("save_hint_no_changes");
  } else if (blockedByAck) {
    hintText = t("save_hint_ack_required");
  } else if (missingComment) {
    hintText = t("save_hint_comment_required");
  }

  const isDisabled = isPending || !isDirty || missingComment || blockedByAck;
  const showHint = hintText !== null && hintText !== t("save_hint_no_changes");

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        aria-describedby={showHint ? hintId : undefined}
        className={cn(
          "inline-flex h-11 items-center gap-1.5 rounded-md bg-accent px-3 text-sm text-white",
          "hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <Check className="h-3.5 w-3.5" aria-hidden />
        {isPending ? t("save_hint_saving") : saveLabel}
      </button>
      {showHint && (
        <p
          id={hintId}
          role="status"
          className="text-[11px] text-text-muted leading-snug"
        >
          {hintText}
        </p>
      )}
    </div>
  );
}
